import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Logo } from '@/components/ui/Logo';
import { ProfileAvatar } from '@/components/profiles/ProfileAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Shield, BookOpen, LogOut, ChevronDown, User, Menu, CreditCard, Bell, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BetaBadge } from '@/components/release/BetaBadge';
import { profileApi } from '@/services/profileApi';
import { contentApi } from '@/services/adminApi';

const AVATAR_CACHE_KEY = 'five_ai_avatar_url';
const AVATAR_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'font-medium transition-colors',
    isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary'
  );

const mobileNavLinks: { to: string; label: string; state?: { fromNav: true } }[] = [
  { to: '/', label: 'Home', state: { fromNav: true } },
  { to: '/coach', label: 'Coach' },
  { to: '/learning-plan', label: 'Learning Plan' },
  { to: '/features', label: 'Features' },
  { to: '/about', label: 'About' },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Fetch profile picture URL efficiently — cache in localStorage for 30 min
  const fetchAvatar = useCallback(async () => {
    if (!isAuthenticated || !user?.email) return;
    try {
      // Check cache first
      const cached = localStorage.getItem(AVATAR_CACHE_KEY);
      if (cached) {
        const { url, ts } = JSON.parse(cached);
        if (Date.now() - ts < AVATAR_CACHE_TTL && url) {
          setAvatarUrl(url);
          return;
        }
      }
      // Fetch profile to get profilePictureId
      const profile = await profileApi.getByEmail(user.email);
      const picId = profile?.profilePictureId;
      if (!picId) return;
      // Fetch the content URL
      const content = await contentApi.getProfilePicture(picId);
      const url = content?.fileUrl;
      if (url) {
        setAvatarUrl(url);
        localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify({ url, ts: Date.now() }));
      }
    } catch {
      // Silent — avatar fallback to initials
    }
  }, [isAuthenticated, user?.email]);

  useEffect(() => { fetchAvatar(); }, [fetchAvatar]);

  return (
    <header 
      className="bg-background/95 backdrop-blur-md border-b border-border fixed top-0 left-0 right-0 z-50 shadow-sm pt-[env(safe-area-inset-top)]"
      role="banner"
    >
      <div className="container-custom max-w-full">
        <div className="flex items-center justify-between h-14 sm:h-16 gap-2 min-w-0">
          <Link
            to="/"
            state={{ fromNav: true }}
            className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0"
            aria-label="Five AI Home"
          >
            <Logo variant="full" className="h-9 sm:h-10 md:h-12 w-auto shrink-0" />
            <BetaBadge className="shrink-0" />
          </Link>

          {/* Mobile nav: hamburger + dropdown */}
          <DropdownMenu open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden shrink-0 h-9 w-9"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[min(280px,100vw-2rem)] rounded-xl shadow-lg border-border/80 bg-popover mt-2">
              {mobileNavLinks.map(({ to, label, state }) => (
                <DropdownMenuItem
                  key={to}
                  onClick={() => {
                    navigate(to, { state: state ?? undefined });
                    setMobileNavOpen(false);
                  }}
                  className="cursor-pointer rounded-lg py-3 text-base"
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <nav className="hidden md:flex items-center space-x-8 shrink-0" role="navigation" aria-label="Main navigation">
            <NavLink to="/" state={{ fromNav: true }} className={navLinkClass}>
              Home
            </NavLink>
            <NavLink to="/coach" className={navLinkClass}>
              Coach
            </NavLink>
            <NavLink to="/learning-plan" className={navLinkClass}>
              Learning Plan
            </NavLink>
            <NavLink to="/features" className={navLinkClass}>
              Features
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              About
            </NavLink>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0 min-w-0">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/login')}
              className="hidden md:flex"
              title="Admin Panel"
              aria-label="Go to admin panel"
            >
              <Shield className="h-5 w-5" />
            </Button>
            {isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
                    aria-label="Open user menu"
                  >
                    <div className="relative">
                      <ProfileAvatar
                        src={avatarUrl}
                        displayName={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                        size="sm"
                        className="h-9 w-9 text-sm ring-2 ring-border/80 hover:ring-primary/50 transition-shadow shadow-md"
                      />
                      {/* Active indicator — green dot */}
                      <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-foreground max-w-[120px] truncate">
                      {user.firstName ?? user.email?.split('@')[0] ?? 'Account'}
                    </span>
                    <ChevronDown className="hidden sm:block h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl shadow-lg border-border/80 bg-popover">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium truncate">
                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Signed in'}
                      </p>
                      {user.email && (
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer rounded-lg gap-2">
                    <User className="h-4 w-4" />
                    My profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/learning-plan')} className="cursor-pointer rounded-lg gap-2">
                    <BookOpen className="h-4 w-4" />
                    Learning plan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/billing')} className="cursor-pointer rounded-lg gap-2">
                    <CreditCard className="h-4 w-4" />
                    Billing & Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings/notifications')} className="cursor-pointer rounded-lg gap-2">
                    <Bell className="h-4 w-4" />
                    Notification Preferences
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings/privacy')} className="cursor-pointer rounded-lg gap-2">
                    <Settings className="h-4 w-4" />
                    Privacy & Data
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="cursor-pointer rounded-lg gap-2 text-muted-foreground focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  aria-label="Sign in"
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/onboarding')}
                  aria-label="Get started with Five AI"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
